import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map, shareReplay, tap } from "rxjs/operators";

import { Country } from "src/app/shared/models/country.interface";
import { environment } from "src/environments/environment";
import { sortAsc } from "src/app/shared/utils/sort.util";
import { SelectOption } from "src/app/shared/models/select-option.interface";
import { formatDataForSelect } from "src/app/shared/utils/format-select-data.util";

@Injectable({
	providedIn: "root"
})
export class CountriesService {
	private http = inject(HttpClient);

	private readonly countries$ = this.http.get<Country[]>(`${environment.apiUri}/v1/resources/countries`).pipe(
		tap((countries: Country[]) => {
			sortAsc(countries, ["country", "name"]);
		}),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	getCountries(): Observable<Country[]> {
		return this.countries$;
	}

	getCountryOptions(): Observable<SelectOption[]> {
		return this.getCountries().pipe(
			map((countries: Country[]) => {
				return countries.map(country => {
					const option: SelectOption = {
						value: country?.country.code,
						text: country?.country.name,
						imagePath: `assets/svg-country-flags/svg/${country?.country.code.toLowerCase()}.svg`
					};

					return formatDataForSelect(option);
				});
			})
		);
	}

	getCountryByCode(countryCode = ""): Observable<Country[]> {
		return this.getCountries().pipe(
			map((countries: Country[]) => {
				return countries.filter(country => country.country.code === countryCode);
			})
		);
	}

	filterCountryByCode(countries: Country[], countryCode = ""): SelectOption {
		const country = countries.filter(country => country.country.code === countryCode)[0];

		return {
			value: country ? country.country.code : "",
			text: country ? country.country.name : "",
			imagePath: country ? `assets/svg-country-flags/svg/${country?.country.code.toLowerCase()}.svg` : "",
			valueArray: []
		};
	}

	defaultCountry(countries: SelectOption[], countryCode: string): SelectOption {
		const country: SelectOption | undefined = countries.find(country => country.value == countryCode);
		if (country) {
			return country;
		} else {
			return {} as SelectOption;
		}
	}
}
